import React from 'react';
import { Box, Typography, Breadcrumbs, Link, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';

/**
 * Reusable page header component with title, subtitle, and optional breadcrumbs
 * 
 * @param {Object} props
 * @param {string} props.title - Main page title
 * @param {string} props.subtitle - Optional subtitle or description
 * @param {Array} props.breadcrumbs - Optional array of breadcrumb objects: [{name: 'Home', path: '/'}, {name: 'Current', path: ''}]
 * @param {React.ReactNode} props.action - Optional action button or component to display in the header
 */
const PageHeader = ({ title, subtitle, breadcrumbs, action }) => {
  return (
    <Box sx={{ mb: 4 }}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return isLast ? (
              <Typography key={index} color="text.primary">
                {crumb.name}
              </Typography>
            ) : (
              <Link 
                key={index} 
                component={RouterLink} 
                to={crumb.path} 
                underline="hover" 
                color="inherit"
              >
                {crumb.name}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}
      
      <Box 
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 1
        }}
      >
        <Typography variant="h4" component="h1" fontWeight="500">
          {title}
        </Typography>
        
        {action && (
          <Box sx={{ mt: { xs: 2, sm: 0 } }}>
            {action}
          </Box>
        )}
      </Box>
      
      {subtitle && (
        <Typography variant="body1" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
};

export default PageHeader;
